import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Textarea } from 'components/ui/textarea';
import { adminDailyPulse } from 'services/api/adminDailyPulseClient';
import type { DailyPulseTopic } from 'services/api/dailyPulseClient';
import type {
  AdminDailyPulseQuestion,
  AdminDailyPulseQuestionWrite,
  AdminDailyPulseResponseType,
} from 'services/api/adminDailyPulseClient';

const typeLabels: Record<AdminDailyPulseResponseType, string> = {
  rating: '1–10 rating', boolean: 'Yes / No', number: 'Number', duration: 'Duration', time: 'Time of day',
};
const selectClass = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';
const topicLabels: Record<DailyPulseTopic, string> = {
  mindset: 'Mindset', connection: 'Connection', movement: 'Movement', sleep: 'Sleep',
  nutrition: 'Nutrition', focus: 'Focus', money: 'Money', other: 'Other',
};

const newQuestion = (order: number): AdminDailyPulseQuestionWrite => ({
  key: '', prompt: '', response_type: 'boolean', section: 'Personal Action', topic: 'other',
  low_label: '', high_label: '', unit: '', min_value: null, max_value: null,
  step: 1, display_order: order, default_enabled: false, is_active: true,
});

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    return Object.entries(data).map(([field, value]) => {
      const message = Array.isArray(value) ? value.join(' ') : String(value);
      return field === 'detail' ? message : `${field.replace(/_/g, ' ')}: ${message}`;
    }).join(' ');
  }
  return 'Unable to save this change. Please try again.';
}

export default function DailyPulseQuestions() {
  const [questions, setQuestions] = useState<AdminDailyPulseQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [section, setSection] = useState('all');
  const [topic, setTopic] = useState('all');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('active');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AdminDailyPulseQuestion | null>(null);
  const [draft, setDraft] = useState(newQuestion(1));
  const [editorError, setEditorError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try { setQuestions(await adminDailyPulse.list()); }
    catch { setLoadError('Unable to load Daily Pulse questions. Please try again.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const sections = useMemo(() => Array.from(new Set(questions.map((q) => q.section))).sort(), [questions]);
  const filtered = useMemo(() => questions.filter((q) => (
    (section === 'all' || q.section === section)
    && (topic === 'all' || (q.topic ?? 'other') === topic)
    && (type === 'all' || q.response_type === type)
    && (status === 'all' || q.is_active === (status === 'active'))
    && (!search.trim() || `${q.prompt} ${q.key}`.toLowerCase().includes(search.trim().toLowerCase()))
  )).sort((a, b) => a.display_order - b.display_order || a.key.localeCompare(b.key)),
  [questions, section, topic, type, status, search]);

  const replaceQuestion = (question: AdminDailyPulseQuestion) => setQuestions((current) => (
    current.some((item) => item.id === question.id)
      ? current.map((item) => item.id === question.id ? question : item)
      : [...current, question]
  ));

  const openEditor = (question: AdminDailyPulseQuestion | null) => {
    setEditing(question);
    if (question) {
      const { id, version, is_legacy, has_answers, ...values } = question;
      setDraft({ ...values, topic: values.topic ?? 'other' });
    } else {
      setDraft(newQuestion(Math.max(0, ...questions.map((q) => q.display_order)) + 1));
    }
    setEditorError('');
    setEditorOpen(true);
  };
  const change = <K extends keyof AdminDailyPulseQuestionWrite>(key: K, value: AdminDailyPulseQuestionWrite[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };
  const changeType = (responseType: AdminDailyPulseResponseType) => setDraft((current) => ({
    ...current, response_type: responseType,
    low_label: responseType === 'rating' ? 'Not at all' : '',
    high_label: responseType === 'rating' ? 'Very much' : '',
    unit: responseType === 'duration' ? 'minutes' : '',
    min_value: ['number', 'duration'].includes(responseType) ? 0 : null,
    max_value: responseType === 'duration' ? 1440 : null,
    step: 1,
  }));

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setEditorError('');
    if (draft.min_value !== null && draft.max_value !== null && draft.min_value > draft.max_value) {
      setEditorError('Maximum value must be greater than or equal to minimum value.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...draft, key: draft.key.trim(), prompt: draft.prompt.trim(), section: draft.section.trim(), unit: draft.unit.trim() };
      const saved = editing
        ? await adminDailyPulse.update(editing.id, { ...payload, version: editing.version })
        : await adminDailyPulse.create(payload);
      replaceQuestion(saved);
      setEditorOpen(false);
      setNotice(editing ? 'Question updated.' : 'Question created.');
    } catch (error) { setEditorError(errorMessage(error)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (question: AdminDailyPulseQuestion) => {
    setBusyId(question.id);
    setActionError('');
    try {
      replaceQuestion(await adminDailyPulse.update(question.id, {
        version: question.version, is_active: !question.is_active,
      }));
      setNotice(question.is_active ? 'Question archived. Saved answers are preserved.' : 'Question reactivated.');
    } catch (error) { setActionError(errorMessage(error)); }
    finally { setBusyId(null); }
  };

  const numeric = draft.response_type === 'number' || draft.response_type === 'duration';
  const locked = Boolean(editing?.has_answers);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Daily Pulse Questions</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Manage the daily question bank. Default questions apply before a member customizes their Pulse.
            Every question remains optional; the shared reflection box is separate.
          </p>
        </div>
        <Button size="auto" onClick={() => openEditor(null)} disabled={loading || Boolean(loadError)}>Add question</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1"><Label htmlFor="pulse-admin-search">Search questions</Label>
          <Input id="pulse-admin-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by wording or key" /></div>
        <div className="space-y-1"><Label htmlFor="pulse-admin-section">Section</Label>
          <select id="pulse-admin-section" className={selectClass} value={section} onChange={(event) => setSection(event.target.value)}>
            <option value="all">All sections</option>{sections.map((item) => <option key={item} value={item}>{item || 'Uncategorized'}</option>)}
          </select></div>
        <div className="space-y-1"><Label htmlFor="pulse-admin-topic">Topic</Label>
          <select id="pulse-admin-topic" className={selectClass} value={topic} onChange={(event) => setTopic(event.target.value)}>
            <option value="all">All topics</option>{Object.entries(topicLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></div>
        <div className="space-y-1"><Label htmlFor="pulse-admin-type">Answer type</Label>
          <select id="pulse-admin-type" className={selectClass} value={type} onChange={(event) => setType(event.target.value)}>
            <option value="all">All types</option>{Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></div>
        <div className="space-y-1"><Label htmlFor="pulse-admin-status">Status</Label>
          <select id="pulse-admin-status" className={selectClass} value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="active">Active</option><option value="archived">Archived</option><option value="all">All statuses</option>
          </select></div>
      </div>
      {notice && <p role="status" className="text-sm text-muted-foreground">{notice}</p>}
      {actionError && <p role="alert" className="text-sm text-destructive">{actionError} Refresh the page to load the latest version before retrying.</p>}
      {loading ? <p role="status">Loading questions…</p> : loadError ? (
        <div role="alert"><p>{loadError}</p><Button variant="outline" onClick={() => void load()}>Retry</Button></div>
      ) : <>
        <p className="text-sm text-muted-foreground">{filtered.length} questions shown · {questions.filter((q) => q.is_active && q.default_enabled).length} active defaults</p>
        {filtered.length === 0 && <p className="rounded-lg border p-6 text-muted-foreground">No questions match these filters.</p>}
        <ul className="space-y-3">
          {filtered.map((question) => <li key={question.id} className="flex flex-wrap items-start justify-between gap-4 rounded-lg border bg-background p-4">
            <div className="min-w-0 flex-1 basis-64 space-y-2">
              <p className="font-medium">{question.prompt}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{topicLabels[question.topic ?? 'other']}</span><span>·</span>
                <span>{question.section || 'Uncategorized'}</span><span>· {typeLabels[question.response_type]}{question.unit ? ` (${question.unit})` : ''}</span>
                <span>· Order {question.display_order}</span>
                {question.default_enabled && <Badge variant="secondary">Default</Badge>}
                {!question.is_active && <Badge variant="outline">Archived</Badge>}
                {question.is_legacy && <Badge variant="outline">Existing member question</Badge>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="auto" aria-label={`Edit ${question.prompt}`} onClick={() => openEditor(question)} disabled={busyId !== null}>Edit</Button>
              <Button variant="ghost" size="auto" aria-label={`${question.is_active ? 'Archive' : 'Reactivate'} ${question.prompt}`} disabled={busyId !== null} onClick={() => void toggleActive(question)}>
                {busyId === question.id ? 'Saving…' : question.is_active ? 'Archive' : 'Reactivate'}
              </Button>
            </div>
          </li>)}
        </ul>
      </>}

      <Dialog open={editorOpen} onOpenChange={(open) => { if (!saving) setEditorOpen(open); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" closeDisabled={saving}>
          <DialogTitle>{editing ? 'Edit Daily Pulse question' : 'Add Daily Pulse question'}</DialogTitle>
          <DialogDescription>Set the question and answer format. Saved answers keep their original wording and values.</DialogDescription>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1"><Label htmlFor="pulse-prompt">Question wording</Label>
              <Textarea id="pulse-prompt" required maxLength={240} value={draft.prompt} onChange={(event) => change('prompt', event.target.value)} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1"><Label htmlFor="pulse-key">Stable key</Label>
                <Input id="pulse-key" required maxLength={64} pattern="([a-zA-Z0-9_]|-)+" disabled={Boolean(editing)} value={draft.key} onChange={(event) => change('key', event.target.value)} />
                <p className="text-xs text-muted-foreground">Letters, numbers, hyphens or underscores. Cannot change after creation.</p></div>
              <div className="space-y-1"><Label htmlFor="pulse-section">Question section</Label>
                <Input id="pulse-section" required maxLength={80} list="pulse-sections" value={draft.section} onChange={(event) => change('section', event.target.value)} />
                <datalist id="pulse-sections">{sections.map((item) => <option key={item} value={item} />)}</datalist></div>
              <div className="space-y-1"><Label htmlFor="pulse-topic">Discovery topic</Label>
                <select id="pulse-topic" className={selectClass} value={draft.topic} onChange={(event) => change('topic', event.target.value as DailyPulseTopic)}>
                  {Object.entries(topicLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <p className="text-xs text-muted-foreground">Helps members find this question when exploring their Daily Pulse.</p></div>
              <div className="space-y-1"><Label htmlFor="pulse-response-type">Response type</Label>
                <select id="pulse-response-type" className={selectClass} disabled={locked} value={draft.response_type} onChange={(event) => changeType(event.target.value as AdminDailyPulseResponseType)}>
                  {Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select></div>
              <div className="space-y-1"><Label htmlFor="pulse-order">Display order</Label>
                <Input id="pulse-order" type="number" min="0" max="32767" step="1" required value={draft.display_order} onChange={(event) => change('display_order', Number(event.target.value))} /></div>
            </div>
            {locked && <p className="text-xs text-muted-foreground">Response type and unit are locked because this question has saved answers. Create a new question to change their meaning.</p>}
            {draft.response_type === 'rating' && <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1"><Label htmlFor="pulse-low">Label for 1</Label><Input id="pulse-low" required maxLength={80} value={draft.low_label} onChange={(event) => change('low_label', event.target.value)} /></div>
              <div className="space-y-1"><Label htmlFor="pulse-high">Label for 10</Label><Input id="pulse-high" required maxLength={80} value={draft.high_label} onChange={(event) => change('high_label', event.target.value)} /></div>
            </div>}
            {numeric && <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1"><Label htmlFor="pulse-unit">Unit</Label><Input id="pulse-unit" required maxLength={40} disabled={locked || draft.response_type === 'duration'} value={draft.unit} placeholder="e.g. steps, oz, USD" onChange={(event) => change('unit', event.target.value)} /></div>
              <div className="space-y-1"><Label htmlFor="pulse-step">Increment</Label><Input id="pulse-step" type="number" min={draft.response_type === 'duration' ? 1 : 0.0001} step={draft.response_type === 'duration' ? 1 : 'any'} required value={draft.step} onChange={(event) => change('step', Number(event.target.value))} /></div>
              <div className="space-y-1"><Label htmlFor="pulse-min">Minimum (optional)</Label><Input id="pulse-min" type="number" min={draft.response_type === 'duration' ? 0 : undefined} step={draft.response_type === 'duration' ? 1 : 'any'} value={draft.min_value ?? ''} onChange={(event) => change('min_value', event.target.value === '' ? null : Number(event.target.value))} /></div>
              <div className="space-y-1"><Label htmlFor="pulse-max">Maximum (optional)</Label><Input id="pulse-max" type="number" min={draft.response_type === 'duration' ? 0 : undefined} step={draft.response_type === 'duration' ? 1 : 'any'} value={draft.max_value ?? ''} onChange={(event) => change('max_value', event.target.value === '' ? null : Number(event.target.value))} /></div>
            </div>}
            <div className="flex items-center gap-3"><Switch id="pulse-default" disabled={Boolean(editing?.is_legacy)} checked={draft.default_enabled} onCheckedChange={(checked) => change('default_enabled', checked)} /><Label htmlFor="pulse-default">Default question</Label></div>
            <p className="text-xs text-muted-foreground">{editing?.is_legacy ? 'This question is retained for existing members and cannot become a new default.' : 'Defaults do not replace a member’s saved selections.'}</p>
            <div className="flex items-center gap-3"><Switch id="pulse-active" checked={draft.is_active} onCheckedChange={(checked) => change('is_active', checked)} /><Label htmlFor="pulse-active">Active in Daily Pulse</Label></div>
            <div className="space-y-2 rounded-lg border bg-muted/30 p-4" aria-label="Answer preview">
              <p className="text-xs font-semibold text-muted-foreground">PREVIEW</p><p>{draft.prompt || 'Your question wording'}</p>
              {draft.response_type === 'boolean' ? <div className="flex gap-2"><Button type="button" variant="outline" disabled>Yes</Button><Button type="button" variant="outline" disabled>No</Button></div>
                : draft.response_type === 'rating' ? <div><div className="flex flex-wrap gap-1">{Array.from({ length: 10 }, (_, index) => <span key={index} className="rounded border px-2 py-1 text-sm">{index + 1}</span>)}</div><p className="mt-1 text-xs">1: {draft.low_label} · 10: {draft.high_label}</p></div>
                  : draft.response_type === 'duration' ? <div className="grid grid-cols-2 gap-3">
                    <label className="space-y-1 text-sm">Hours<Input aria-label="Preview hours" type="number" disabled placeholder="0" /></label>
                    <label className="space-y-1 text-sm">Minutes<Input aria-label="Preview minutes" type="number" disabled placeholder="0" /></label>
                  </div>
                  : <div className="flex items-center gap-2"><Input aria-label="Preview answer" type={draft.response_type === 'time' ? 'time' : 'number'} disabled placeholder="Enter a value" /><span className="text-sm">{draft.unit}</span></div>}
              {draft.response_type === 'duration' && <p className="text-xs text-muted-foreground">Bounds and increments above are measured in total minutes.</p>}
              {draft.response_type === 'time' && <p className="text-xs text-muted-foreground">Local time of day.</p>}
            </div>
            {editorError && <div role="alert" className="text-sm text-destructive">{editorError}</div>}
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" disabled={saving} onClick={() => setEditorOpen(false)}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save question'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
